import { cn } from "@/lib/utils"

/** A pointer that travelled further than this was a drag, not a tap. */
const TAP_SLOP_PX = 8

/**
 * OKS-1022 — the badge detail sheet's drag cue: the short bar at the top of
 * the sheet (UX Spec §3), and the one hint that the sheet can grow.
 *
 * Two jobs, and they pull against each other:
 *
 * 1. **Swipe origin.** Base UI's drawer refuses to start a swipe whose
 *    hit-test target is inside `button, a, input, select, textarea, label,
 *    [role="button"]` (`utils/useSwipeDismiss` `DEFAULT_IGNORE_SELECTOR`). So
 *    the element a finger or mouse actually lands on must NOT be a button:
 *    that is the outer `<div>` (it carries the testid the specs and the PO
 *    walk drag and tap).
 * 2. **Tap / keyboard / screen-reader toggle.** The inner `<button>` is the
 *    accessible control (name, `aria-expanded`, focus, Enter/Space). It is
 *    `pointer-events-none`, so pointers hit-test to the outer div, while
 *    keyboard and assistive-tech activation still dispatch `click` on the
 *    button, which bubbles to the ONE `onClick` on the div. There is no
 *    handler on the button itself, so nothing toggles twice.
 *
 * **Pointer taps are detected from the window, not from `click`.** On a
 * mouse press the drawer calls `setPointerCapture` on its popup
 * (`useSwipeDismiss`), so the release — and the click Chrome derives from it —
 * is delivered to the popup, never to this element. The press is therefore
 * recorded here and resolved by a one-shot capture-phase `pointerup` listener
 * on `window`: within {@link TAP_SLOP_PX} of the press it is a tap and toggles;
 * further, it was a swipe and does nothing. `onClick` toggles only for
 * keyboard / assistive-tech activation (`detail === 0`), so a pointer tap that
 * does reach `click` (touch, which the drawer does not capture) never toggles
 * twice.
 *
 * A tap toggles peek ↔ expanded and never closes the sheet — closing stays on
 * ×, Escape, the backdrop and a swipe down (PRD §5 Q4). Named "Grabber", not
 * "Handle": Base UI's `Drawer.Handle` is an imperative detached-trigger
 * handle (PRD §5 Q2). The bar keeps vaul's grab-bar metrics (5px × 48px, 70%
 * opacity) on a 44px-tall target, anchored to the top where the bar sat.
 */
export function BadgeSheetGrabber({
  expanded,
  onToggle,
  label,
  className,
}: {
  /** Whether the sheet is at its expanded detent. */
  expanded: boolean
  /** Toggle between the peek and expanded detents. */
  onToggle: () => void
  /** Accessible name (already localised). */
  label: string
  className?: string
}) {
  return (
    <div
      data-testid="badge-detail-grabber"
      onPointerDown={(event) => {
        if (event.button !== 0) return
        const startX = event.clientX
        const startY = event.clientY
        const view = event.currentTarget.ownerDocument.defaultView ?? window
        const finish = (end: PointerEvent) => {
          view.removeEventListener("pointerup", finish, true)
          view.removeEventListener("pointercancel", cancel, true)
          if (
            Math.hypot(end.clientX - startX, end.clientY - startY) <=
            TAP_SLOP_PX
          ) {
            onToggle()
          }
        }
        const cancel = () => {
          view.removeEventListener("pointerup", finish, true)
          view.removeEventListener("pointercancel", cancel, true)
        }
        view.addEventListener("pointerup", finish, true)
        view.addEventListener("pointercancel", cancel, true)
      }}
      onClick={(event) => {
        // Keyboard / assistive-tech activation of the inner button only.
        if (event.detail === 0) onToggle()
      }}
      className={cn(
        "group/grabber flex h-11 w-12 cursor-grab touch-none items-start justify-center active:cursor-grabbing",
        className,
      )}
    >
      <button
        type="button"
        aria-label={label}
        aria-expanded={expanded}
        className="focus-visible:ring-ring pointer-events-none flex w-full justify-center rounded-full pt-1 pb-2 outline-none focus-visible:ring-2"
      >
        <span
          aria-hidden="true"
          className="bg-border block h-[5px] w-12 rounded-full opacity-70 group-hover/grabber:opacity-100 group-active/grabber:opacity-100"
        />
      </button>
    </div>
  )
}
